import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import HostContactEditor from '../components/HostContactEditor';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { PageLoad, ErrorMsg, StatusBadge, Progress } from '../components/ui';
import { usePrograms } from '../context/ProgramsContext';
import { CA_PROVINCES } from '../utils/provinces';
import { hostCityOptions, hostMatchesCity, hostMatchesProvince } from '../utils/hostLocationFilters';
import PandaDocModal from '../components/PandaDocModal';
import HostTemplatePicker from '../components/HostTemplatePicker';
import HostAgreementUploads from '../components/HostAgreementUploads';

const STATUS_META = {
  none:        { label: 'No Agreement',       color: '#B3261E', bg: '#FBE7E5', dot: '#D14343' },
  draft:       { label: 'Draft',              color: '#B47200', bg: '#FBF1DD', dot: '#F5A623' },
  sent:        { label: 'Awaiting Signature', color: '#B47200', bg: '#FBF1DD', dot: '#F5A623' },
  viewed:      { label: 'Viewed',             color: '#1D4ED8', bg: '#DBEAFE', dot: '#3B82F6' },
  host_signed: { label: 'Awaiting Dean',      color: '#7A4F00', bg: '#FBE9D2', dot: '#D9892A' },
  executed:    { label: 'Active Partner',     color: '#1F8A5B', bg: '#E6F3EC', dot: '#1F8A5B' },
  declined:    { label: 'Declined',           color: '#B3261E', bg: '#FBE7E5', dot: '#D14343' },
};

/* ── Custom Select Dropdown ──────────────────────────── */
function SelectDrop({ value, onChange, options, minWidth = 130 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = options.find(o => o.value === value) || options[0];

  return (
    <div className={`h-select${open ? ' open' : ''}`} ref={ref} style={{ minWidth }}>
      <button className="h-select-btn" onClick={() => setOpen(o => !o)}>
        <span>{current.label}</span>
        <ChevronDownIcon />
      </button>
      {open && (
        <div className="h-select-menu">
          {options.map(o => (
            <button
              key={o.value}
              className={`h-select-opt${o.value === value ? ' selected' : ''}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Filter Bar ──────────────────────────────────────── */
function FilterBar({ search, setSearch, regionFilter, setRegionFilter, cityFilter, setCityFilter, agreementFilter, setAgreementFilter, programFilter, setProgramFilter, programs, PROGRAM_NAMES, cities, view, setView, isAdmin }) {
  const provinceOpts = [
    { value: '', label: 'All provinces' },
    ...CA_PROVINCES.map(p => ({ value: p.abbr, label: p.name })),
  ];
  const cityOpts = [
    { value: '', label: 'All cities' },
    ...cities.map(c => ({ value: c, label: c })),
  ];
  const agreementOpts = [
    { value: '', label: 'All agreements' },
    { value: 'executed', label: 'Active Partner' },
    { value: 'sent', label: 'Awaiting Signature' },
    { value: 'host_signed', label: 'Awaiting Dean' },
    { value: 'none', label: 'No Agreement' },
  ];
  const programOpts = [
    { value: '', label: 'All programs' },
    ...programs.map(p => ({ value: p, label: PROGRAM_NAMES[p] || p })),
  ];

  return (
    <div className="h-filter-bar">
      <div className="h-search-wrap">
        <SearchIcon />
        <input
          type="text"
          className="h-search-input"
          placeholder="Search organizations, contacts or programs…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button className="h-search-clear" onClick={() => setSearch('')} aria-label="Clear">
            <CloseIcon size={14} />
          </button>
        )}
        <kbd className="h-search-kbd">⌘K</kbd>
      </div>
      <div className="h-filter-group">
        <SelectDrop value={regionFilter}    onChange={setRegionFilter}    options={provinceOpts}  minWidth={140} />
        <SelectDrop value={cityFilter}      onChange={setCityFilter}      options={cityOpts}      minWidth={140} />
        <SelectDrop value={agreementFilter} onChange={setAgreementFilter} options={agreementOpts} minWidth={152} />
        {isAdmin && (
          <SelectDrop value={programFilter}   onChange={setProgramFilter}   options={programOpts}   minWidth={145} />
        )}
      </div>
      <div className="h-view-toggle">
        <button className={view === 'grid' ? 'active' : ''} onClick={() => setView('grid')} title="Grid view"><GridIcon /></button>
        <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')} title="List view"><ListIcon /></button>
      </div>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────── */
export default function HostsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { programMap: PROGRAM_NAMES, programs: allPrograms } = usePrograms();
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [agreementFilter, setAgreementFilter] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [sortBy, setSortBy] = useState('az');
  const [view, setView] = useState('grid');
  const [showForm, setShowForm] = useState(false);
  const [openHostId, setOpenHostId] = useState(null);
  const [error, setError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const hostParam = searchParams.get('host');

  // Deep-link: /hosts?host=<id> opens that host's slide-over. Keyed on the param so it
  // fires even when already on the Hosts page (e.g. clicking a notification from here).
  useEffect(() => {
    if (hostParam) {
      setOpenHostId(parseInt(hostParam, 10));
      searchParams.delete('host');
      setSearchParams(searchParams, { replace: true });
    }
  }, [hostParam]);

  const fetchHosts = async () => {
    try {
      const res = await api.get('/hosts');
      setHosts(res.data);
    } catch { setError('Failed to load hosts'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchHosts(); }, []);

  const programs = useMemo(
    () => [...new Set(hosts.flatMap(h => h.programs_accepted || []))].sort(),
    [hosts]
  );

  const cities = useMemo(
    () => hostCityOptions(hosts, regionFilter),
    [hosts, regionFilter]
  );

  const handleRegionFilterChange = (province) => {
    setRegionFilter(province);
    const availableCities = hostCityOptions(hosts, province);
    if (cityFilter && !availableCities.some(city => hostMatchesCity({ city }, cityFilter))) {
      setCityFilter('');
    }
  };

  useEffect(() => {
    if (!loading && cityFilter && !cities.some(city => hostMatchesCity({ city }, cityFilter))) {
      setCityFilter('');
    }
  }, [loading, cities, cityFilter]);

  const filtered = useMemo(() => hosts.filter(h => {
    const fullName = [h.contact_first_name, h.contact_last_name].filter(Boolean).join(' ');
    if (search && !h.org_name.toLowerCase().includes(search.toLowerCase()) &&
        !fullName.toLowerCase().includes(search.toLowerCase())) return false;
    if (agreementFilter && h.agreement_status !== agreementFilter) return false;
    if (programFilter && !(h.programs_accepted || []).includes(programFilter)) return false;
    if (!hostMatchesProvince(h, regionFilter)) return false;
    if (!hostMatchesCity(h, cityFilter)) return false;
    return true;
  }), [hosts, search, agreementFilter, programFilter, regionFilter, cityFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (sortBy === 'recent') {
      arr.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    } else {
      arr.sort((a, b) => (a.org_name || '').localeCompare(b.org_name || ''));
    }
    return arr;
  }, [filtered, sortBy]);

  const active  = hosts.filter(h => h.agreement_status === 'executed').length;
  const pending = hosts.filter(h => ['sent', 'host_signed'].includes(h.agreement_status)).length;
  const isAdmin = user.role === 'admin';
  const isHostSpecialist = !!user.is_host_specialist;

  const stats = [
    { eyebrow: 'TOTAL',   value: hosts.length, label: 'Partner organizations',     delta: '+12 MTD', fill: 92 },
    { eyebrow: 'ACTIVE',  value: active,        label: 'Active agreements',          delta: '+4 this week', fill: hosts.length ? Math.round(active / hosts.length * 100) : 0 },
    { eyebrow: 'PENDING', value: pending,       label: 'Awaiting signature or dean', delta: '−2 this week', fill: hosts.length ? Math.round(pending / hosts.length * 100) : 0 },
  ];

  if (loading) return <PageLoad />;

  return (
    <>
      {/* ── Hero strip ───────────────────────────────── */}
      <div className="h-hero">
        {/* Decorative SVG rings */}
        <div className="h-hero-decor" aria-hidden="true">
          <svg viewBox="0 0 800 320" preserveAspectRatio="xMaxYMid slice" width="100%" height="100%">
            <g fill="none" stroke="rgba(245,166,35,0.10)" strokeWidth="1">
              <circle cx="720" cy="160" r="80"/>
              <circle cx="720" cy="160" r="140"/>
              <circle cx="720" cy="160" r="200"/>
              <circle cx="720" cy="160" r="260"/>
            </g>
            <g fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1">
              <circle cx="540" cy="40" r="180"/>
              <circle cx="540" cy="40" r="240"/>
            </g>
          </svg>
        </div>

        <div className="h-hero-inner">
          {/* Top row: breadcrumb + actions */}
          <div className="h-hero-top">
            <div>
              <div className="h-hero-crumb">
                <span>Workspace</span>
                <span className="h-crumb-sep">/</span>
                <span>Hosts</span>
              </div>
              <h1 className="h-hero-title">
                Host <span className="h-hero-accent">Organizations</span>
              </h1>
              <p className="h-hero-lede">
                Manage employer partners, agreements and student placements across all programs.
              </p>
            </div>
            <div className="h-hero-actions">
              <button className="h-btn-ghost">
                <FilterIcon /> Export CSV
              </button>
              {isAdmin && (
                <button className="h-btn-accent" onClick={() => setShowForm(s => !s)}>
                  <PlusIcon /> Add Host
                </button>
              )}
            </div>
          </div>

          {/* KPI stat cards */}
          <div className="h-stats">
            {stats.map((s, i) => (
              <div key={i} className="h-stat-card">
                <div className="h-stat-top">
                  <span className="h-stat-eyebrow">{s.eyebrow}</span>
                  <span className="h-stat-delta">{s.delta}</span>
                </div>
                <div className="h-stat-num">{s.value}</div>
                <div className="h-stat-label">{s.label}</div>
                <div className="h-stat-bar">
                  <div className="h-stat-bar-fill" style={{ width: s.fill + '%' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content (overlaps hero) ───────────────────── */}
      <div className="h-content">
        <div className="h-content-inner">
          {error && <ErrorMsg>{error}</ErrorMsg>}

          {showForm && isAdmin && (
            <HostForm
              onSave={() => { setShowForm(false); fetchHosts(); }}
              onCancel={() => setShowForm(false)}
            />
          )}

          {/* Filter bar */}
          <FilterBar
            search={search} setSearch={setSearch}
            regionFilter={regionFilter} setRegionFilter={handleRegionFilterChange}
            cityFilter={cityFilter} setCityFilter={setCityFilter}
            agreementFilter={agreementFilter} setAgreementFilter={setAgreementFilter}
            programFilter={programFilter} setProgramFilter={setProgramFilter}
            programs={programs} PROGRAM_NAMES={PROGRAM_NAMES}
            cities={cities}
            view={view} setView={setView}
            isAdmin={isAdmin}
          />

          {/* Section meta */}
          <div className="h-section-meta">
            <div className="h-count">
              Showing <strong>{sorted.length}</strong> of <strong>{hosts.length}</strong> organizations
            </div>
            <div className="h-sort">
              Sort by:{' '}
              <button
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: sortBy === 'az' ? 700 : 400, color: sortBy === 'az' ? 'var(--ink-1)' : 'var(--ink-3)', fontSize: 'inherit' }}
                onClick={() => setSortBy('az')}
              >A–Z</button>
              <span style={{ margin: '0 6px', color: 'var(--ink-3)' }}>·</span>
              <button
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: sortBy === 'recent' ? 700 : 400, color: sortBy === 'recent' ? 'var(--ink-1)' : 'var(--ink-3)', fontSize: 'inherit' }}
                onClick={() => setSortBy('recent')}
              >Recently updated ↓</button>
            </div>
          </div>

          {/* Card grid / List */}
          {sorted.length === 0 ? (
            <div className="h-empty">
              <p>{search || regionFilter || agreementFilter || programFilter
                ? 'No hosts match the current filters.'
                : 'No host organizations yet.'}</p>
            </div>
          ) : view === 'grid' ? (
            <div className="host-grid">
              {sorted.map(host => (
                <HostCard key={host.id} host={host} PROGRAM_NAMES={PROGRAM_NAMES} onOpen={setOpenHostId} />
              ))}
            </div>
          ) : (
            <div className="host-list">
              <div className="host-list-head">
                <div>Organization</div>
                <div>Primary Contact</div>
                <div>Phone</div>
                <div>Programs</div>
                <div>Status</div>
                <div />
              </div>
              {sorted.map(host => (
                <HostRow key={host.id} host={host} PROGRAM_NAMES={PROGRAM_NAMES} onOpen={setOpenHostId} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Slide-over detail panel ───────────────────── */}
      {openHostId && (
        <DetailPanel
          hostId={openHostId}
          user={user}
          allPrograms={allPrograms}
          PROGRAM_NAMES={PROGRAM_NAMES}
          isAdmin={isAdmin}
          isHostSpecialist={isHostSpecialist}
          onClose={() => setOpenHostId(null)}
          navigate={navigate}
          onHostUpdated={fetchHosts}
        />
      )}
    </>
  );
}

/* ── Host Card ───────────────────────────────────────── */
function HostCard({ host, PROGRAM_NAMES, onOpen }) {
  const meta = STATUS_META[host.agreement_status] || STATUS_META.none;
  const contactName = [host.contact_first_name, host.contact_last_name].filter(Boolean).join(' ');
  const initials = contactName
    ? contactName.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : host.org_name?.slice(0, 2).toUpperCase() || '??';

  return (
    <article
      className="host-card"
      style={{ '--status-color': meta.dot, '--status-bg': meta.bg }}
      onClick={() => onOpen(host.id)}
    >
      <div className="card-rail" style={{ background: meta.dot }} />

      <header className="card-head">
        <div className="card-title-wrap">
          <h3 className="card-title">{host.org_name}</h3>
          {host.campus_region && (
            <div className="card-loc">
              <PinIcon /> <span>{host.campus_region}</span>
            </div>
          )}
        </div>
        <div className="status-pill" style={{ background: meta.bg, color: meta.color }}>
          <span className="status-dot" style={{ background: meta.dot }} />
          <span>{meta.label}</span>
        </div>
      </header>

      {(contactName || host.contact_email) && (
        <div className="card-contacts">
          <div className="contact-row">
            <div className="contact-avatar">{initials}</div>
            <div className="contact-info">
              {contactName && (
                <div className="contact-name">
                  {contactName}
                  {host.contact_position && (
                    <span className="contact-role"> · {host.contact_position}</span>
                  )}
                </div>
              )}
              {host.contact_email && (
                <a
                  className="contact-email"
                  href={`mailto:${host.contact_email}`}
                  onClick={e => e.stopPropagation()}
                >
                  <MailSmIcon /> {host.contact_email}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {host.contact_phone && (
        <div className="card-meta-row">
          <div className="meta-phone"><PhoneIcon /> <span>{host.contact_phone}</span></div>
        </div>
      )}

      {host.programs_accepted?.length > 0 && (
        <div className="card-programs">
          {host.programs_accepted.map(p => (
            <span key={p} className="program-pill">{PROGRAM_NAMES[p] || p}</span>
          ))}
        </div>
      )}

      {(host.program_statuses || []).map(ps => {
        const multi = (host.program_statuses || []).length > 1;
        const pfx = multi ? `${ps.program_code}: ` : '';
        if (ps.accepting_review_pending) {
          return (
            <div key={ps.program_code} className="card-accepting accepting-review">
              <span className="accepting-dot" />{pfx}Sent to Host Specialist for review
            </div>
          );
        }
        if (!ps.accepting_updated_at) return null;
        return (
          <div key={ps.program_code} className={`card-accepting ${ps.accepting_students ? 'accepting-yes' : 'accepting-no'}`}>
            <span className="accepting-dot" />
            {pfx}
            {ps.accepting_students
              ? <>Accepting{ps.accepting_capacity ? <strong> {ps.accepting_capacity}</strong> : null} student{ps.accepting_capacity !== 1 ? 's' : ''}</>
              : 'Not accepting students'}
            <span className="accepting-date">
              · {new Date(ps.accepting_updated_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
        );
      })}

      <footer className="card-footer">
        <button
          className="card-action"
          onClick={e => { e.stopPropagation(); onOpen(host.id); }}
        >
          View details <ArrowRightIcon />
        </button>
        <div className="card-quick">
          <button title="Send email" onClick={e => { e.stopPropagation(); onOpen(host.id); }}>
            <MailSmIcon size={14} />
          </button>
          <button title="Call" onClick={e => { e.stopPropagation(); if (host.contact_phone) window.location.href = `tel:${host.contact_phone}`; }}>
            <PhoneIcon size={14} />
          </button>
          <button title="Open panel" onClick={e => { e.stopPropagation(); onOpen(host.id); }}>
            <PlusIcon size={14} />
          </button>
        </div>
      </footer>
    </article>
  );
}

/* ── Host Row (list view) ────────────────────────────── */
function HostRow({ host, PROGRAM_NAMES, onOpen }) {
  const meta = STATUS_META[host.agreement_status] || STATUS_META.none;
  const contactName = [host.contact_first_name, host.contact_last_name].filter(Boolean).join(' ');

  return (
    <div
      className="host-row"
      style={{ '--status-color': meta.dot }}
      onClick={() => onOpen(host.id)}
    >
      <div className="row-rail" />
      <div className="row-name">
        <div className="row-title">
          {host.direct_apply && <SparkleIcon />}
          {host.org_name}
        </div>
        {host.campus_region && (
          <div className="row-loc"><PinIcon /> {host.campus_region}</div>
        )}
      </div>
      <div className="row-contact">
        {contactName && <div className="row-c-name">{contactName}</div>}
        {host.contact_email && <div className="row-c-email">{host.contact_email}</div>}
      </div>
      <div className="row-phone">{host.contact_phone || '—'}</div>
      <div className="row-programs">
        {(host.programs_accepted || []).slice(0, 2).map((p, i) => (
          <span key={i} className="program-pill sm">{PROGRAM_NAMES[p] || p}</span>
        ))}
        {(host.programs_accepted || []).length > 2 && (
          <span className="program-pill sm muted">+{host.programs_accepted.length - 2}</span>
        )}
      </div>
      <div className="row-status">
        <div className="status-pill" style={{ background: meta.bg, color: meta.color }}>
          <span className="status-dot" style={{ background: meta.dot }} />
          <span>{meta.label}</span>
        </div>
      </div>
      <button
        className="row-action"
        onClick={e => { e.stopPropagation(); onOpen(host.id); }}
      >
        <ArrowRightIcon />
      </button>
    </div>
  );
}

/* ── Detail Panel (slide-over) ───────────────────────── */
const AGREEMENT_LABELS = {
  none:        { label: 'No Agreement',       color: '#B3261E', bg: '#FBE7E5', dot: '#D14343' },
  draft:       { label: 'Draft',              color: '#B47200', bg: '#FBF1DD', dot: '#F5A623' },
  sent:        { label: 'Awaiting Signature', color: '#B47200', bg: '#FBF1DD', dot: '#F5A623' },
  viewed:      { label: 'Viewed',             color: '#1D4ED8', bg: '#DBEAFE', dot: '#3B82F6' },
  host_signed: { label: 'Awaiting Dean',      color: '#7A4F00', bg: '#FBE9D2', dot: '#D9892A' },
  executed:    { label: 'Active Partner',     color: '#1F8A5B', bg: '#E6F3EC', dot: '#1F8A5B' },
  declined:    { label: 'Declined',           color: '#B3261E', bg: '#FBE7E5', dot: '#D14343' },
};

/* ── Accepting Panel (inline in slide-over) ──────────── */
const HS_BTN_PRIMARY = { background: '#D14343', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' };
const HS_BTN_GHOST   = { background: 'var(--bg-elev)', color: 'var(--ink-2)', border: '1px solid var(--line)', padding: '6px 12px', borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' };

function AcceptingPanel({ host, isHostSpecialist, onUpdated, onHostUpdated }) {
  const statuses = host.program_statuses || [];
  if (statuses.length === 0) {
    return <div style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>No programs configured for this host yet.</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {statuses.map(ps => (
        <ProgramAcceptingRow
          key={ps.program_code}
          hostId={host.id}
          ps={ps}
          multi={statuses.length > 1}
          isHostSpecialist={isHostSpecialist}
          onUpdated={onUpdated}
          onHostUpdated={onHostUpdated}
        />
      ))}
    </div>
  );
}

function ProgramAcceptingRow({ hostId, ps, multi, isHostSpecialist, onUpdated, onHostUpdated }) {
  const [capacity, setCapacity]       = useState(ps.accepting_capacity ?? '');
  const [rejectSpots, setRejectSpots] = useState(ps.accepting_capacity ?? '');
  const [saving, setSaving]           = useState(false);
  const [confirmOff, setConfirmOff]   = useState(false);

  useEffect(() => {
    setCapacity(ps.accepting_capacity ?? '');
    setRejectSpots(ps.accepting_capacity ?? '');
    setConfirmOff(false);
  }, [ps.accepting_students, ps.accepting_capacity, ps.accepting_review_pending]);

  const accepting = ps.accepting_students ?? false;
  const pending   = !!ps.accepting_review_pending;

  const fmt = d => d ? new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  const asOf      = fmt(ps.accepting_updated_at);
  const flaggedOn = fmt(ps.accepting_review_requested_at);
  const progLabel = ps.program_name || ps.program_code;

  const call = async (body) => {
    setSaving(true);
    try {
      const res = await api.put(`/hosts/${hostId}/accepting`, { program_code: ps.program_code, ...body });
      await onUpdated();
      onHostUpdated?.();
      return res.data;
    } catch { return null; }
    finally { setSaving(false); }
  };

  const wrap = (children) => (
    <div style={multi ? { border: '1px solid var(--line)', borderRadius: 10, padding: '10px 12px' } : undefined}>
      {multi && <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>{progLabel}</div>}
      {children}
    </div>
  );

  // ── Pending review ──────────────────────────────────────────────
  if (pending) {
    return wrap(
      isHostSpecialist ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--status-ready-bg)', border: '1px solid var(--amber)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>Review requested</div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 2 }}>
              A coordinator flagged this host as <strong>not currently accepting students</strong>{flaggedOn ? ` on ${flaggedOn}` : ''}. Contact the host, then decide below.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button style={HS_BTN_PRIMARY} disabled={saving} onClick={() => call({ accepting_students: false })}>
              Confirm — not accepting
            </button>
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>or</span>
            <button style={HS_BTN_GHOST} disabled={saving} onClick={() => call({ accepting_students: true, accepting_capacity: rejectSpots })}>
              No — they are accepting
            </button>
            <input type="number" min={1} className="input" style={{ width: 66, padding: '4px 8px', fontSize: 13 }}
              value={rejectSpots} onChange={e => setRejectSpots(e.target.value)} placeholder="Spots" title="Spots available" />
          </div>
          {saving && <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Saving…</span>}
        </div>
      ) : (
        <div style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--status-danger-bg)', border: '1px solid var(--status-danger-fg)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--status-danger-fg)' }}>Sent to the Host Specialist to confirm</div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 2 }}>
          Flagged as not currently accepting students{flaggedOn ? ` on ${flaggedOn}` : ''}. Awaiting confirmation from the Host Specialist.
        </div>
      </div>
      )
    );
  }

  // ── Normal (not pending) ────────────────────────────────────────
  return wrap(
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={accepting}
          disabled={saving || confirmOff}
          onChange={(e) => {
            if (e.target.checked) call({ accepting_students: true, accepting_capacity: capacity });
            else setConfirmOff(true);
          }}
        />
        <span style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500 }}>
          {accepting ? 'Currently accepting students' : 'Not accepting students'}
        </span>
        {saving && <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Saving…</span>}
      </label>

      {accepting && !confirmOff && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 46 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)' }}>Spots available</label>
          <input
            type="number" min={1} className="input" style={{ width: 72, padding: '4px 8px', fontSize: 13 }}
            value={capacity} onChange={e => setCapacity(e.target.value)}
            onBlur={() => call({ accepting_students: true, accepting_capacity: capacity })}
            placeholder="Any"
          />
        </div>
      )}

      {asOf && !confirmOff && (
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)', paddingLeft: 46 }}>Last updated {asOf}</div>
      )}

      {/* Confirmation before marking a host as not accepting */}
      {confirmOff && (
        <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg-sunken)', border: '1px solid var(--line)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 6 }}>
            Are you sure this host is not currently accepting students?
          </div>
          {!isHostSpecialist && (
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>
              This will be sent to the Host Specialist to confirm.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={HS_BTN_PRIMARY} disabled={saving} onClick={() => call({ accepting_students: false })}>Yes</button>
            <button style={HS_BTN_GHOST} disabled={saving} onClick={() => setConfirmOff(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Host notes & call log (program-scoped) ──────────── */
function HostNotesSection({ hostId }) {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`/hosts/${hostId}/notes`); setNotes(r.data); }
    catch { setNotes([]); }
  }, [hostId]);
  useEffect(() => { load(); }, [load]);

  const add = async (kind) => {
    const body = text.trim();
    if (kind === 'note' && !body) return;
    setSaving(true);
    try {
      const r = await api.post(`/hosts/${hostId}/notes`, { kind, body });
      setNotes(prev => [r.data, ...(prev || [])]);
      setText('');
    } catch { /* ignore */ } finally { setSaving(false); }
  };
  const fmt = d => new Date(d).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        <textarea className="input" rows={2} value={text} onChange={e => setText(e.target.value)}
          placeholder="Log a call or add a note…" style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }} />
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => add('call')}>Log call</button>
          <button className="btn btn-ghost btn-sm" disabled={saving || !text.trim()} onClick={() => add('note')}>Add note</button>
        </div>
      </div>
      {notes === null ? (
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>Loading…</div>
      ) : notes.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>No notes yet.</div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {notes.map(n => (
            <li key={n.id} style={{ borderBottom: '1px solid var(--line)', paddingBottom: 8 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 12.5, color: 'var(--ink)' }}>{n.kind === 'call' ? 'Call logged' : 'Note'}</strong>
                {n.program_code
                  ? <span className="program-pill sm">{n.program_code}</span>
                  : <span className="program-pill sm muted">Host-level</span>}
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{n.author_name || 'Staff'} · {fmt(n.created_at)}</span>
              </div>
              {n.body && <div style={{ fontSize: 13, color: 'var(--ink-2)', whiteSpace: 'pre-wrap', marginTop: 3 }}>{n.body}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Host conversation (program-scoped email thread + in-app compose) ──────────
   A coordinator's email to a shared host stays inside their program: the thread
   here is already scoped server-side, and sending stamps the program so the
   host's reply routes back to the same program (never leaks to the other PC). */
function HostConversation({ host, user, PROGRAM_NAMES }) {
  const hostId = host.id;
  const isPriv = user.role === 'admin' || !!user.is_host_specialist;
  const hostProgs = host.programs_accepted || [];
  const sendable = isPriv ? hostProgs : (user.program_codes || []).filter(c => hostProgs.includes(c));

  const [log, setLog] = useState(null);
  const [program, setProgram] = useState(sendable[0] || '');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try { const r = await api.get(`/hosts/${hostId}/email-log`); setLog(r.data); }
    catch { setLog([]); }
  }, [hostId]);
  useEffect(() => { load(); }, [load]);

  // Conversation = coordinator↔host direct messages + inbound replies (system
  // mail like nurture/agreement stays in Recent Activity, not here).
  const thread = (log || [])
    .filter(e => e.direction === 'inbound' || e.trigger_type === 'direct')
    .sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));

  const fmt = d => new Date(d).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  const send = async () => {
    setErr('');
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    try {
      await api.post(`/hosts/${hostId}/message`, { subject: subject.trim(), body: body.trim(), program_code: program || undefined });
      setSubject(''); setBody('');
      await load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to send');
    } finally { setSending(false); }
  };

  if (!host.contact_email) {
    return <div style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>No contact email on file for this host.</div>;
  }
  if (sendable.length === 0) {
    return <div style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>This host is not set up for your program, so you can’t start a conversation here.</div>;
  }

  return (
    <div>
      {/* Compose */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {sendable.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Program:</span>
            <select className="input" value={program} disabled={sending} onChange={e => {
              if ((subject || body) && !window.confirm('Changing programs clears the current draft. Continue?')) return;
              setProgram(e.target.value); setSubject(''); setBody('');
            }}
              style={{ fontSize: 13, padding: '4px 8px', width: 'auto' }}>
              {sendable.map(c => <option key={c} value={c}>{PROGRAM_NAMES[c] || c}</option>)}
            </select>
          </div>
        )}
        {sendable.length === 1 && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            Sending as <span className="program-pill sm">{PROGRAM_NAMES[program] || program}</span> — the host’s reply returns to this program only.
          </div>
        )}
        <HostTemplatePicker key={`${hostId}:${program}`} host={host} program={program} user={user}
          programName={PROGRAM_NAMES[program] || program} disabled={sending}
          onSent={load} onDraft={draft => { setSubject(draft.subject); setBody(draft.body); setErr(''); }} />
        <input id="host-compose" className="input" value={subject} onChange={e => setSubject(e.target.value)}
          placeholder="Subject…" style={{ fontSize: 13 }} />
        <textarea className="input" rows={3} value={body} onChange={e => setBody(e.target.value)}
          placeholder={`Message to ${host.contact_email}…`} style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }} />
        {err && <div style={{ fontSize: 12, color: '#B3261E' }}>{err}</div>}
        <div>
          <button className="btn btn-primary btn-sm" disabled={sending || !subject.trim() || !body.trim()} onClick={send}>
            {sending ? 'Sending…' : 'Send email'}
          </button>
        </div>
      </div>

      {/* Thread */}
      {log === null ? (
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>Loading…</div>
      ) : thread.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>No messages yet.</div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {thread.map(m => {
            const inbound = m.direction === 'inbound';
            return (
              <li key={m.id} style={{
                borderLeft: `3px solid ${inbound ? '#1F8A5B' : '#3B82F6'}`,
                background: 'var(--line-2)', borderRadius: 8, padding: '8px 10px',
              }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 3 }}>
                  <strong style={{ fontSize: 12.5, color: 'var(--ink)' }}>{inbound ? 'Host reply' : 'Sent'}</strong>
                  {m.program_code
                    ? <span className="program-pill sm">{m.program_code}</span>
                    : <span className="program-pill sm muted">Host-level</span>}
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{fmt(m.sent_at)}</span>
                </div>
                {m.subject && <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink-2)' }}>{m.subject}</div>}
                {m.metadata?.body_text && (
                  <div style={{ fontSize: 12.5, color: 'var(--ink-2)', whiteSpace: 'pre-wrap', marginTop: 2 }}>{m.metadata.body_text}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function DetailPanel({ hostId, user, allPrograms, PROGRAM_NAMES, isAdmin, isHostSpecialist, onClose, navigate, onHostUpdated }) {
  const [host, setHost] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPandaDoc, setShowPandaDoc] = useState(false);

  const load = useCallback(async () => {
    try {
      const [hostRes, agreeRes, logRes] = await Promise.all([
        api.get(`/hosts/${hostId}`),
        api.get(`/hosts/${hostId}/agreement`),
        api.get(`/hosts/${hostId}/email-log`),
      ]);
      setHost(hostRes.data);
      setAgreement(agreeRes.data);
      setEmailLog(logRes.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [hostId]);

  useEffect(() => { load(); }, [load]);

  /* Close on Escape */
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const agreeMeta = AGREEMENT_LABELS[agreement?.status] || AGREEMENT_LABELS.none;
  const contactName = host ? [host.contact_first_name, host.contact_last_name].filter(Boolean).join(' ') : '';
  const initials = contactName
    ? contactName.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : host?.org_name?.slice(0, 2).toUpperCase() || '??';
  const canSendAgreement = !agreement?.status || ['none', 'draft', 'declined'].includes(agreement.status);

  /* Build unified activity timeline from emails + PandaDoc events */
  const activities = useMemo(() => {
    const items = [];

    /* Email log */
    (emailLog || []).forEach(e => {
      const labelMap = {
        host_nurture:    { label: 'Partner check-in email sent',         dot: '#3B82F6', detail: `To: ${e.recipient}` },
        agreement_sent:  { label: 'Agreement notification email sent',   dot: '#F5A623', detail: `To: ${e.recipient}` },
      };
      const cfg = labelMap[e.trigger_type] || { label: e.trigger_type, dot: '#9CA3AF', detail: `To: ${e.recipient}` };
      items.push({ label: cfg.label, detail: cfg.detail, dot: cfg.dot, ts: e.sent_at, kind: 'email' });
    });

    /* PandaDoc timeline */
    const tl = agreement?.timeline || {};
    if (tl.sentAt)
      items.push({ label: 'Site agreement sent via PandaDoc',   dot: '#F5A623', detail: 'Awaiting contact signature', ts: tl.sentAt,      kind: 'doc' });
    if (tl.viewedAt)
      items.push({ label: 'Agreement opened and viewed',         dot: '#8B5CF6', detail: 'Contact opened the document', ts: tl.viewedAt,   kind: 'doc' });
    if (agreement?.status === 'host_signed' && tl.viewedAt && !tl.completedAt && !tl.declinedAt)
      items.push({ label: 'Contact signed — awaiting dean',      dot: '#D9892A', detail: 'Pending MCG countersignature', ts: tl.viewedAt,  kind: 'doc' });
    if (tl.completedAt)
      items.push({ label: 'Agreement fully executed',            dot: '#1F8A5B', detail: 'All parties have signed',      ts: tl.completedAt, kind: 'doc' });
    if (tl.declinedAt)
      items.push({ label: 'Agreement declined',                  dot: '#EF4444', detail: 'Contact declined to sign',    ts: tl.declinedAt,  kind: 'doc' });

    /* Sort newest first */
    return items.sort((a, b) => new Date(b.ts) - new Date(a.ts));
  }, [emailLog, agreement]);

  return (
    <>
      {/* Scrim */}
      <div className="dp-scrim" onClick={onClose} />

      {/* Panel */}
      <aside className="dp" role="dialog" aria-label={host?.org_name || 'Host details'}>
        {/* Dark header */}
        <div className="dp-head">
          <button className="dp-close" onClick={onClose} aria-label="Close">
            <CloseIcon size={18} />
          </button>
          {loading ? (
            <div style={{ color: 'rgba(255,255,255,0.5)', paddingTop: 32, fontSize: 13 }}>Loading…</div>
          ) : host ? (
            <>
              <div className="dp-eyebrow">
                <PinIcon /><span>{host.campus_region || '—'}</span>
              </div>
              <h2 className="dp-title">{host.org_name}</h2>
              <div className="dp-status">
                <div className="status-pill" style={{ background: 'rgba(255,255,255,0.12)', color: 'white' }}>
                  <span className="status-dot" style={{ background: agreeMeta.dot }} />
                  <span>{agreeMeta.label}</span>
                </div>
              </div>
            </>
          ) : null}
        </div>

        {/* Scrollable body */}
        {!loading && host && (
          <div className="dp-body">

            {/* Quick actions */}
            <section className="dp-section">
              <h4>Quick actions</h4>
              <div className="dp-actions">
                <button
                  className="dp-btn primary"
                  style={{ background: 'var(--navy)' }}
                  onClick={() => navigate(`/students`)}
                >
                  <UserIcon /> Assign student
                </button>
                {host.contact_email && (
                  <button
                    className="dp-btn"
                    onClick={() => {
                      const el = document.getElementById('host-compose');
                      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
                    }}
                  >
                    <MailSmIcon size={14} /> Email contact
                  </button>
                )}
                {agreement?.viewUrl ? (
                  <a className="dp-btn" href={agreement.viewUrl} target="_blank" rel="noopener noreferrer">
                    <DocIcon /> View agreement
                  </a>
                ) : isAdmin && canSendAgreement ? (
                  <button className="dp-btn" onClick={() => setShowPandaDoc(true)}>
                    <DocIcon /> Send agreement
                  </button>
                ) : null}
                {(isAdmin || isHostSpecialist) && host.contact_email && (
                  <AvailabilityCheckButton hostId={host.id} />
                )}
                <button className="dp-btn" onClick={() => navigate(`/hosts/${host.id}`)}>
                  <ArrowRightIcon size={14} /> Full details
                </button>
              </div>
            </section>

            {/* Contact */}
            {(isAdmin || isHostSpecialist) && (
              <section className="dp-section">
                <h4>Contact details</h4>
                <HostContactEditor key={host.id} host={host} onSaved={() => { load(); onHostUpdated?.(); }} />
              </section>
            )}
            {(contactName || host.contact_email) && (
              <section className="dp-section">
                <h4>Contacts</h4>
                <div className="dp-contacts">
                  <div className="dp-contact">
                    <div className="dp-avatar">{initials}</div>
                    <div>
                      {contactName && (
                        <div className="dp-c-name">
                          {contactName}
                          {host.contact_position && (
                            <span style={{ fontWeight: 400, color: 'var(--ink-3)', fontSize: 12 }}> · {host.contact_position}</span>
                          )}
                        </div>
                      )}
                      {host.contact_email && (
                        <a className="dp-c-email" href={`mailto:${host.contact_email}`}>
                          <MailSmIcon size={11} /> {host.contact_email}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Phone */}
            {host.contact_phone && (
              <section className="dp-section">
                <h4>Phone</h4>
                <div className="dp-phone"><PhoneIcon size={14} /> {host.contact_phone}</div>
              </section>
            )}

            {/* Always show uploads, even when no PandaDoc agreement exists. */}
            <section className="dp-section">
              <h4>Agreement</h4>
              {agreement && agreement.status !== 'none' && (
                <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div className="status-pill" style={{ background: agreeMeta.bg, color: agreeMeta.color }}>
                    <span className="status-dot" style={{ background: agreeMeta.dot }} />
                    <span>{agreeMeta.label}</span>
                  </div>
                  {agreement.viewUrl && (
                    <a href={agreement.viewUrl} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 12, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      View in PandaDoc <ArrowRightIcon size={11} />
                    </a>
                  )}
                </div>
                {agreement.timeline?.sentAt && (
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 6 }}>
                    Sent {new Date(agreement.timeline.sentAt).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}
                    {agreement.timeline.completedAt && ` · Executed ${new Date(agreement.timeline.completedAt).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}`}
                  </div>
                )}
                </>
              )}
              <div style={{ marginTop: agreement && agreement.status !== 'none' ? 14 : 0 }}>
                <HostAgreementUploads
                  key={host.id}
                  hostId={host.id}
                  canManage={isAdmin || isHostSpecialist}
                  actionClassName="dp-btn"
                  compact
                />
              </div>
            </section>

            {/* Programs */}
            {host.programs_accepted?.length > 0 && (
              <section className="dp-section">
                <h4>Programs ({host.programs_accepted.length})</h4>
                <div className="dp-programs">
                  {host.programs_accepted.map(p => (
                    <span key={p} className="program-pill">{PROGRAM_NAMES[p] || p}</span>
                  ))}
                </div>
              </section>
            )}

            {/* Accepting students */}
            <section className="dp-section">
              <h4>Accepting Students</h4>
              <AcceptingPanel host={host} isAdmin={isAdmin} isHostSpecialist={isHostSpecialist} onUpdated={load} onHostUpdated={onHostUpdated} />
            </section>

            {/* Assigned students */}
            {host.students?.length > 0 && (
              <section className="dp-section">
                <h4>Assigned Students ({host.students.length})</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {host.students.map(s => (
                    <div key={s.id}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: 'var(--line-2)', borderRadius: 10, cursor: 'pointer' }}
                      onClick={() => navigate(`/students/${s.id}`)}
                    >
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{s.first_name} {s.last_name}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{s.program_code}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <StatusBadge status={s.status} />
                        <ArrowRightIcon size={13} />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Conversation (program-scoped email thread + in-app compose) */}
            <section className="dp-section">
              <h4>Conversation</h4>
              <HostConversation key={host.id} host={host} user={user} PROGRAM_NAMES={PROGRAM_NAMES} />
            </section>

            {/* Notes & call log (program-scoped: your program's notes + shared host notes) */}
            <section className="dp-section">
              <h4>Notes &amp; call log</h4>
              <HostNotesSection hostId={host.id} />
            </section>

            {/* Recent activity — merged email log + PandaDoc events */}
            <section className="dp-section">
              <h4>Recent Activity</h4>
              {activities.length === 0 ? (
                <div style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>No activity recorded yet.</div>
              ) : (
                <ul className="dp-activity">
                  {activities.map((ev, i) => (
                    <li key={i}>
                      <span className="dp-act-dot" style={{ background: ev.dot }} />
                      <div>
                        <strong>{ev.label}</strong>
                        <span>
                          {new Date(ev.ts).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}
                          {' · '}{ev.detail}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Notes */}
            {host.notes && (
              <section className="dp-section">
                <h4>Notes</h4>
                <div style={{ fontSize: 13.5, color: 'var(--ink-2)', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{host.notes}</div>
              </section>
            )}

          </div>
        )}
      </aside>

      {/* PandaDoc send modal */}
      {showPandaDoc && host && (
        <PandaDocModal
          host={host}
          programs={allPrograms.filter(p => (host.programs_accepted || []).includes(p.code))}
          onClose={() => setShowPandaDoc(false)}
          onSent={() => { setShowPandaDoc(false); load(); onHostUpdated(); }}
        />
      )}
    </>
  );
}

/* ── Availability check-in button (host slide-over) ──── */
function AvailabilityCheckButton({ hostId }) {
  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const send = async () => {
    setState('sending');
    try { await api.post(`/hosts/${hostId}/availability-check`); setState('sent'); }
    catch { setState('error'); }
  };
  const label = state === 'sending' ? 'Sending…'
    : state === 'sent' ? 'Check-in sent ✓'
    : state === 'error' ? 'Failed — retry'
    : 'Ask availability';
  return (
    <button className="dp-btn" onClick={send} disabled={state === 'sending' || state === 'sent'}>
      <MailSmIcon size={14} /> {label}
    </button>
  );
}

/* ── Add Host Form ───────────────────────────────────── */
function HostForm({ onSave, onCancel }) {
  const { programs: allPrograms, programMap: PROGRAM_NAMES } = usePrograms();
  const programs = allPrograms.map(p => p.code);
  const [form, setForm] = useState({
    org_name: '', contact_first_name: '', contact_last_name: '', contact_position: '',
    contact_email: '', contact_phone: '', address: '', city: '', province: '',
    postal_code: '', campus_region: '', capacity: '', programs_accepted: [], notes: '', website: '',
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hosts', { ...form, capacity: parseInt(form.capacity) || null });
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create host');
    }
  };

  const toggleProgram = (p) => {
    setForm(prev => ({
      ...prev,
      programs_accepted: prev.programs_accepted.includes(p)
        ? prev.programs_accepted.filter(x => x !== p)
        : [...prev.programs_accepted, p],
    }));
  };

  return (
    <div className="card card-pad" style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 16, fontSize: 15 }}>Add New Host Organization</div>
      {error && <ErrorMsg>{error}</ErrorMsg>}
      <form onSubmit={handleSubmit} className="form-grid-3" style={{ gap: 12 }}>
        <Field label="Organization Name" value={form.org_name} onChange={v => setForm({ ...form, org_name: v })} required />
        <Field label="First Name" value={form.contact_first_name} onChange={v => setForm({ ...form, contact_first_name: v })} />
        <Field label="Last Name" value={form.contact_last_name} onChange={v => setForm({ ...form, contact_last_name: v })} />
        <Field label="Contact Position" value={form.contact_position} onChange={v => setForm({ ...form, contact_position: v })} placeholder="e.g. HR Manager" />
        <Field label="Contact Email" type="email" value={form.contact_email} onChange={v => setForm({ ...form, contact_email: v })} />
        <Field label="Contact Phone" value={form.contact_phone} onChange={v => setForm({ ...form, contact_phone: v })} />
        <Field label="Capacity" type="number" value={form.capacity} onChange={v => setForm({ ...form, capacity: v })} />
        <Field label="Street Address" value={form.address} onChange={v => setForm({ ...form, address: v })} placeholder="e.g. 4774 Westwinds Dr NE" />
        <Field label="City" value={form.city} onChange={v => setForm(f => ({ ...f, city: v, campus_region: v || f.campus_region }))} placeholder="e.g. Calgary" />
        <Field label="Province" value={form.province} onChange={v => setForm({ ...form, province: v })} placeholder="e.g. Alberta" />
        <Field label="Postal Code" value={form.postal_code} onChange={v => setForm({ ...form, postal_code: v })} placeholder="e.g. T2P 1G1" />
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Programs Accepted</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {programs.map(p => (
              <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.programs_accepted.includes(p)} onChange={() => toggleProgram(p)} />
                {PROGRAM_NAMES[p] || p}
              </label>
            ))}
          </div>
        </div>
        <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary">Create Host</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required = false, placeholder = '' }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} className="input" required={required} placeholder={placeholder} />
    </div>
  );
}

/* ── Icons ───────────────────────────────────────────── */
const Ico = ({ d, size = 18, stroke = 1.75 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
    {typeof d === 'string' ? <path d={d} /> : d}
  </svg>
);

const SearchIcon    = ()          => <Ico size={18} d={<g><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></g>} />;
const FilterIcon    = ()          => <Ico size={16} d="M3 5h18l-7 9v6l-4-2v-4z" />;
const PlusIcon      = ({size=16}) => <Ico size={size} d="M12 5v14M5 12h14" />;
const ChevronDownIcon = ()        => <Ico size={14} d="M6 9l6 6 6-6" />;
const CloseIcon     = ({size=14}) => <Ico size={size} d="M6 6l12 12M18 6L6 18" />;
const PinIcon       = ()          => <Ico size={11} d={<g><path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z"/><circle cx="12" cy="9" r="2.5"/></g>} />;
const MailSmIcon    = ({size=11}) => <Ico size={size} d={<g><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></g>} />;
const PhoneIcon     = ({size=12}) => <Ico size={size} d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8 9.6a16 16 0 0 0 6 6l1.2-1.2a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z" />;
const ArrowRightIcon = ({size=12}) => <Ico size={size} d="M5 12h14M13 6l6 6-6 6" />;
const GridIcon      = ()          => <Ico size={16} d={<g><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></g>} />;
const ListIcon      = ()          => <Ico size={16} d={<g><path d="M8 6h13M8 12h13M8 18h13"/><circle cx="4" cy="6" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="18" r="1"/></g>} />;
const SparkleIcon   = ()          => <Ico size={12} d={<g><path d="M12 3l1.7 4.7L18.3 9.5l-4.6 1.8L12 16l-1.7-4.7L5.7 9.5l4.6-1.8z"/><path d="M19 16l.7 1.8 1.8.7-1.8.7L19 21l-.7-1.8-1.8-.7 1.8-.7z"/></g>} />;
const UserIcon      = ({size=14}) => <Ico size={size} d={<g><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></g>} />;
const DocIcon       = ({size=14}) => <Ico size={size} d={<g><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/></g>} />;
